// src/pages/Relatorios/RelatorioFinanceiroPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Material-UI imports
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  TextField, // Importação adicionada
} from '@mui/material';
import { Download as DownloadIcon, ArrowBack as ArrowBackIcon, Clear as ClearIcon } from '@mui/icons-material'; // Importação do ícone de limpar filtro
import { DatePicker } from '@mui/x-date-pickers';

// Recharts imports
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

// Services and Hooks imports
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

// Cores para os gráficos
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Função para formatar valores monetários
const formatarMoeda = (valor) => {
  return new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN'
  }).format(valor);
};

const RelatorioFinanceiroPage = () => {
  // Estados
  const [activeTab, setActiveTab] = useState(0);
  const [relatorio, setRelatorio] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: null,
    dataFim: null,
  });
  const [loading, setLoading] = useState(false);

  // Hooks
  const { id } = useParams();
  const { user } = useAuth();
  const instituicaoId = id || user?.instituicaoId;
  const navigate = useNavigate();

  // Função para carregar o relatório financeiro
  const carregarRelatorioFinanceiro = useCallback(async () => {
    if (!instituicaoId) {
      console.warn('ID da instituição não disponível.');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio.toISOString());
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim.toISOString());

      const response = await api.get(`/relatorios/financeiro/instituicao/${instituicaoId}`, { params });
      setRelatorio(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar relatório financeiro:', error);
      // Opcional: Adicionar notificações ou alertas para o usuário
    } finally {
      setLoading(false);
    }
  }, [instituicaoId, filtros]);

  // Função para exportar o relatório
  const exportarRelatorio = async (formato) => {
    if (!instituicaoId) {
      console.warn('ID da instituição não disponível para exportação.');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio.toISOString());
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim.toISOString());

      const response = await api.get(
        `/relatorios/financeiro/instituicao/${instituicaoId}/export/${formato}`,
        { 
          params,
          responseType: 'blob'
        }
      );

      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-financeiro-${instituicaoId}.${formato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      // Opcional: Adicionar notificações ou alertas para o usuário
    }
  };

  // Função para limpar os filtros
  const limparFiltros = useCallback(() => {
    setFiltros({
      dataInicio: null,
      dataFim: null,
    });
  }, []);

  // Efeito para carregar o relatório ao montar o componente ou ao atualizar filtros/instituição
  useEffect(() => {
    carregarRelatorioFinanceiro();
  }, [carregarRelatorioFinanceiro]);

  // Funções de renderização para cada aba
  const renderResumoFinanceiro = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Valor Total Esperado
            </Typography>
            <Typography variant="h5">
              {formatarMoeda(relatorio.estatisticas.resumoFinanceiro.valorTotalEsperado)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Valor Recebido
            </Typography>
            <Typography variant="h5">
              {formatarMoeda(relatorio.estatisticas.resumoFinanceiro.valorTotalRecebido)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total em Multas
            </Typography>
            <Typography variant="h5">
              {formatarMoeda(relatorio.estatisticas.resumoFinanceiro.valorTotalMultas)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Taxa de Inadimplência
            </Typography>
            <Typography variant="h5">
              {relatorio.estatisticas.inadimplencia.taxaInadimplencia}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderMetodosPagamento = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Métodos de Pagamento
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={Object.entries(relatorio.estatisticas.metodoPagamento).map(([metodo, dados]) => ({
              metodo,
              valor: dados.valor
            }))}
            nameKey="metodo"
            dataKey="valor"
            cx="50%"
            cy="50%"
            outerRadius={80}
            label
          >
            {Object.entries(relatorio.estatisticas.metodoPagamento).map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => formatarMoeda(value)} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );

  const renderTendenciasPagamento = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Tendência de Pagamentos
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={Object.entries(relatorio.estatisticas.tendencias.pagamentosPorMes).map(([mes, valor]) => ({
            mes,
            valor
          }))}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="mes" />
          <YAxis />
          <Tooltip formatter={(value) => formatarMoeda(value)} />
          <Legend />
          <Line type="monotone" dataKey="valor" stroke="#8884d8" name="Valor Recebido" />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );

  const renderDetalhesPorEstudante = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Detalhamento por Estudante
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={relatorio.detalhamentoPorPlano}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="estudante.nome" />
          <YAxis />
          <Tooltip formatter={(value) => formatarMoeda(value)} />
          <Legend />
          <Bar dataKey="valorTotal" fill="#8884d8" name="Valor Total" />
          <Bar dataKey="valorPago" fill="#82ca9d" name="Valor Pago" />
          <Bar dataKey="valorEmAtraso" fill="#ff8042" name="Valor em Atraso" />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  // Verificação de carregamento ou ausência de relatório
  if (loading || !relatorio) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Botão "Voltar" */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')} // Substitua '/dashboard' pela rota correta do seu dashboard
        >
          Voltar ao Dashboard
        </Button>
      </Box>

      {/* Seção de Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Relatório Financeiro
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <DatePicker
            label="Data Início"
            value={filtros.dataInicio}
            onChange={(date) => setFiltros({ ...filtros, dataInicio: date })}
            renderInput={(params) => <TextField {...params} />}
          />
          <DatePicker
            label="Data Fim"
            value={filtros.dataFim}
            onChange={(date) => setFiltros({ ...filtros, dataFim: date })}
            renderInput={(params) => <TextField {...params} />}
          />
          <Button
            variant="contained"
            onClick={carregarRelatorioFinanceiro}
            disabled={loading}
          >
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => exportarRelatorio('excel')}
            startIcon={<DownloadIcon />}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            onClick={limparFiltros}
            startIcon={<ClearIcon />}
            disabled={!filtros.dataInicio && !filtros.dataFim}
          >
            Limpar Filtro
          </Button>
        </Box>
      </Paper>

      {/* Seção de Abas */}
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Resumo" />
        <Tab label="Métodos de Pagamento" />
        <Tab label="Tendências" />
        <Tab label="Detalhamento" />
      </Tabs>

      {/* Conteúdo das Abas */}
      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && renderResumoFinanceiro()}
        {activeTab === 1 && renderMetodosPagamento()}
        {activeTab === 2 && renderTendenciasPagamento()}
        {activeTab === 3 && renderDetalhesPorEstudante()}
      </Box>
    </Box>
  );
};

// Definição de PropTypes (opcional, caso haja props)
RelatorioFinanceiroPage.propTypes = {
  // Defina as propTypes aqui se necessário
};

export default RelatorioFinanceiroPage;
